import axios from 'axios'
import { tokens } from 'locales/tokens';

/**
 * 못 낸 송장 라벨을 알린다.
 *
 * 서버는 낼 수 있는 것만 PDF 로 내고 나머지를 헤더로 알려 준다. 그것을
 * 화면이 보지 않으면 열 장 요청해 여덟 장만 나와도 아무도 모른다 — 창고는
 * 나온 장수를 세지 않고 붙이므로, 빠진 주문이 송장 없이 나간다.
 *
 * 라벨을 뽑는 화면이 셋(패킹완료 · 일괄검수 · 송장 재출력)인데 셋 다 이
 * 인터셉터를 지난다. 여기 한 곳에 두면 화면이 늘어도 저절로 따라온다.
 *
 * 목록은 txt 로 내려받게 한다. 팝업은 닫으면 사라지는데 안 나온 번호는
 * 다시 뽑아야 하는 것이라 손에 남아야 한다.
 */
const notifySkippedLabels = (response: any, $alert: Function, t: Function) => {
    const headers = response.headers || {}
    const header = (name: string) => headers[name] || headers[name.toLowerCase()] || ''

    const count = Number(header('X-Label-Skipped-Count'))
    if (!count) {
        return
    }

    // 헤더에는 ASCII 만 안전해서 서버가 UTF-8 퍼센트 인코딩으로 싣는다.
    let text = ''
    try {
        text = decodeURIComponent(header('X-Label-Skipped-Text'))
    } catch (e) {
        // 인코딩이 깨져도 번호는 알려야 한다. 사유를 몰라도 다시 뽑을 수는 있다.
        text = header('X-Label-Skipped')
    }

    console.warn('[송장라벨] 못 낸 송장', count, text)

    downloadText(`미출력송장_${stamp()}.txt`, text)

    $alert({
        title: t(tokens.common.warning),
        text: `${t(tokens.common.label_partially_printed, { count })}\n\n${text}`,
        icon: 'warn',
    })
}

/** 파일로 떨어뜨린다. 팝업을 닫아도 목록이 남는다 */
const downloadText = (fileName: string, text: string) => {
    const href = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(href)
}

/** 파일명에 넣을 시각. 여러 번 뽑아도 서로 덮어쓰지 않는다 */
const stamp = () => {
    const d = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

/**
 * 서버가 준 오류 문구를 꺼낸다. 없으면 빈 값.
 *
 * PDF·엑셀 요청은 `responseType: 'blob'` 이라 오류 응답도 Blob 으로 들어온다.
 * 그대로 두면 화면이 이유를 모른 채 "서버 오류" 만 띄우는데, 서버는 대개
 * 사람이 읽을 문구를 실어 보낸다.
 */
const readErrorMessage = async (data: any): Promise<string> => {
    if (!data) {
        return ''
    }

    let body = data
    if (body instanceof Blob) {
        try {
            body = JSON.parse(await body.text())
        } catch (e) {
            // JSON 이 아니면 본문이 곧 문구다. 너무 길면 화면을 덮으므로 자른다
            try {
                const text = await data.text()
                return text.length > 300 ? '' : text
            } catch (inner) {
                return ''
            }
        }
    }

    return body?.error || body?.message || ''
}

export const getAxios = (baseURL: string | undefined, $alert: Function, t: Function, setBlock?: Function, setIsExcel?: Function) => {
    //create an axios instance

    const axiosInstance = axios.create({
        baseURL,
        headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
        },
    })

    //요청 타임아웃 설정
    // axiosInstance.defaults.timeout = 5000
    axiosInstance.defaults.withCredentials = true

    //요청 인터셉터 추가
    axiosInstance.interceptors.request.use(
        (config) => {
            const auhtorization = localStorage.getItem('rdmsToken')

            if (!auhtorization) {
                window.location.href = '/#/login'
            }

            config.headers.Authorization = auhtorization

            if (config.responseType === 'blob' && setBlock && setIsExcel) {
                setBlock(true)
                setIsExcel(true)
            }

            return config
        },
        (error) => {
            console.log(error) //디버깅
            return Promise.reject(error)
        },
    )

    //응답 인터셉터 추가
    axiosInstance.interceptors.response.use(
        async (response) => {
            // Content-Type을 대소문자 구분 없이 가져오기
            const contentType = response.headers['content-type'] || response.headers['Content-Type'] || ''
            const contentTypeLower = contentType.toLowerCase()

            const res = response.data
            // Blob 응답인 경우 먼저 처리
            if (res instanceof Blob) {
                if (contentTypeLower === 'application/octet-stream') {
                    const href = URL.createObjectURL(new Blob([res], { type: 'application/octet-stream' }))

                    // create "a" HTML element with href to file & click
                    const link = document.createElement('a')
                    link.href = href
                    link.setAttribute('download', decodeURI(response.headers['timf-file-name'] || response.headers['Timf-File-Name'] || ''))
                    document.body.appendChild(link)
                    link.click()

                    // clean up "a" element & remove ObjectURL
                    document.body.removeChild(link)
                    URL.revokeObjectURL(href)
                    setBlock && setBlock(false)
                    setIsExcel && setIsExcel(false)
                    return
                } else if (contentTypeLower.includes('application/pdf') || contentTypeLower.startsWith('image/')) {
                    // PDF 또는 이미지 타입(PNG, JPEG 등) 처리
                    setBlock && setBlock(false)
                    setIsExcel && setIsExcel(false)
                    // 송장 라벨은 일부만 나올 수 있다. 못 낸 것을 여기서 알린다.
                    notifySkippedLabels(response, $alert, t)
                    return res
                } else {
                    // Content-Type이 없거나 알 수 없는 Blob인 경우 기본 처리 (PDF/이미지로 가정)
                    setBlock && setBlock(false)
                    setIsExcel && setIsExcel(false)
                    return res
                }
            }

            // Blob이 아닌 경우에만 JSON 파싱 시도
            const { isSuccess, error, data } = res

            if (isSuccess) {
                return data
            }else if (isSuccess === undefined) {
              // responseType 이 blob인 경우 에러 처리
                setBlock && setBlock(false)
                setIsExcel && setIsExcel(false)
                return null
            }
            else {
                setBlock && setBlock(false)
                setIsExcel && setIsExcel(false)
                await $alert({
                    title: t(tokens.common.failure),
                    text: error,
                    icon: 'error',
                })
                return null
            }
        },

        async (error) => {
            const { response, config, code } = error

            if (!response && code === 'ERR_NETWORK') {
                await $alert({
                    title: t(tokens.common.network_error),
                    text: t(tokens.common.cannot_connect_server),
                    icon: 'error',
                })
                return
            }

            const { status, data } = response

            switch (status) {
                case 401:
                    localStorage.setItem('rdmsToken', `Bearer ${data.token}`)
                    return axiosInstance(config)
                case 403:
                    window.location.href = '/#/login'
                    return
                default: {
                    // PDF·엑셀을 받는 요청은 responseType 이 blob 이라, 서버가 오류
                    // JSON 을 줘도 Blob 으로 들어온다. 풀지 않으면 화면은 이유를
                    // 모른 채 "서버 오류" 만 띄운다 — 실제로는 "게이트웨이에 접수되지
                    // 않은 송장" 처럼 담당자가 바로 조치할 수 있는 내용이 들어 있다.
                    const message = await readErrorMessage(data)
                    if (message) {
                        setBlock && setBlock(false)
                        setIsExcel && setIsExcel(false)
                        await $alert({
                            title: t(tokens.common.failure),
                            text: message,
                            icon: 'error',
                        })
                        return null
                    }
                    return Promise.reject(error)
                }
            }
        },
    )

    return axiosInstance
}

export const download = async (baseURL: string | undefined, uri: string, fileNm: string) => {
    if (!baseURL) {
        return
    }

    const axiosInstance = axios.create({
        baseURL,
    })

    axiosInstance.defaults.withCredentials = true

    const response = await axios.get(`${baseURL}${uri}`, {
        responseType: 'blob',
    })

    const href = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = href
    document.body.appendChild(link)
    link.setAttribute('download', decodeURI(fileNm ? fileNm : 'download'))
    link.click()

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link)
    URL.revokeObjectURL(href)
}
